import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import useSeo from "@/hooks/useSeo";
import { SectionBadge } from "@/components/Comparison";
import { INSIGHTS } from "@/data/seoContent";
import { ArrowRight, Calendar, BookOpen } from "lucide-react";

export function InsightsIndex() {
  useSeo({
    title: "Business Finance Insights · SwiftCash",
    description: "Practical guides on UK business finance — equipment funding, commercial mortgages, VAT bills, invoice finance and growth capital.",
    path: "/insights",
  });
  return (
    <div className="bg-white text-navy min-h-screen">
      <Navbar />
      <section className="hero-grad py-16 md:py-24">
        <div className="max-w-[1280px] mx-auto px-5 md:px-8">
          <SectionBadge>Insights</SectionBadge>
          <h1 className="font-display font-extrabold text-[44px] md:text-[64px] leading-[1] tracking-[-0.035em] text-navy mt-4">Business finance, <span className="text-emerald">demystified.</span></h1>
          <p className="mt-6 text-[16px] text-navy/70 font-medium max-w-2xl leading-[1.6]">Practical guides on how UK businesses actually fund growth — no jargon, no fluff.</p>
        </div>
      </section>
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-[1280px] mx-auto px-5 md:px-8">
          <div className="grid md:grid-cols-3 gap-5">
            {INSIGHTS.map((a) => (
              <Link key={a.slug} to={`/insights/${a.slug}`} data-testid={`insight-${a.slug}`}
                className="rounded-2xl bg-paper-warm border border-line p-7 hover:border-emerald/40 hover:-translate-y-1 transition-all flex flex-col">
                <div className="inline-flex items-center gap-2 text-emerald-3 font-mono text-[10.5px] uppercase tracking-[0.14em] font-extrabold mb-5">
                  <BookOpen className="h-3 w-3" strokeWidth={2.6} /> Guide
                </div>
                <h3 className="font-display font-extrabold text-[19px] tracking-[-0.02em] text-navy mb-3 leading-[1.25] flex-1">{a.title}</h3>
                <p className="text-[13.5px] text-navy/65 font-medium leading-[1.55] mb-5">{a.intro}</p>
                <div className="flex items-center justify-between pt-4 border-t border-line">
                  <span className="inline-flex items-center gap-1.5 text-[11.5px] text-navy/50 font-mono uppercase tracking-[0.1em] font-bold"><Calendar className="h-3 w-3" strokeWidth={2.4} /> {a.date}</span>
                  <span className="inline-flex items-center gap-1 text-emerald-3 font-mono text-[10.5px] uppercase tracking-[0.14em] font-extrabold">Read <ArrowRight className="h-3 w-3" strokeWidth={2.6} /></span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}

export default function InsightArticle() {
  const { slug } = useParams();
  const a = INSIGHTS.find((x) => x.slug === slug);
  const title = a ? `${a.title} · SwiftCash` : "Insight · SwiftCash";
  const description = a?.intro || "SwiftCash business finance insight.";
  const jsonLd = a ? {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": a.title,
    "datePublished": a.date,
    "author": { "@type": "Organization", "name": "SwiftCash" },
    "publisher": { "@type": "Organization", "name": "SwiftCash", "url": "https://swiftca.sh" },
  } : null;
  useSeo({ title, description, path: `/insights/${slug || ""}`, jsonLd, type: "article" });
  if (!a) return <Navigate to="/insights" replace />;

  return (
    <div className="bg-white text-navy min-h-screen">
      <Navbar />
      <article className="max-w-3xl mx-auto px-5 md:px-8 py-14 md:py-20">
        <div className="inline-flex items-center gap-2 text-emerald-3 font-mono text-[10.5px] uppercase tracking-[0.14em] font-extrabold mb-5">
          <BookOpen className="h-3 w-3" strokeWidth={2.6} /> Insight · {a.date}
        </div>
        <h1 className="font-display font-extrabold text-[36px] md:text-[52px] leading-[1.05] tracking-[-0.03em] text-navy mb-6">{a.title}</h1>
        <p className="text-[18px] leading-[1.55] text-navy/70 font-medium mb-10">{a.intro}</p>
        <div className="space-y-6">
          {a.body.map((p, i) => (
            <p key={i} className="text-[16px] leading-[1.75] text-navy/85 font-medium"
               dangerouslySetInnerHTML={{ __html: p.replace(/\*\*(.+?)\*\*/g, '<strong class="text-navy">$1</strong>') }} />
          ))}
        </div>
        <div className="mt-14 pt-10 border-t border-line flex flex-col sm:flex-row gap-3">
          <Link to="/apply" data-testid="insight-cta" className="btn-shine group inline-flex items-center justify-center gap-2 h-12 px-6 rounded-xl bg-emerald text-white font-extrabold text-[14px] hover:bg-emerald-2 shadow-emerald flex-1">
            Get my tailored quote <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" strokeWidth={2.4} />
          </Link>
          <Link to="/insights" className="inline-flex items-center justify-center h-12 px-6 rounded-xl border border-line text-navy/70 font-bold hover:bg-paper-warm text-[13.5px]">
            More insights
          </Link>
        </div>
      </article>
      <Footer />
    </div>
  );
}
